/**
 * Feature Registry — single source of truth for every user-facing feature.
 *
 * Why this exists:
 * Adding a new feature used to require touching 7+ files (OnboardingPreferences,
 * FocusPicker options, useNavItems.buildNavForPrefs, useAvailableNavItems,
 * useFocusMode profiles, HomePage hero conditions, etc.). Forgetting any one
 * of them led to bugs like notes/tasks bleeding into Fitness-only nav bars.
 *
 * How to add a new feature:
 *  1. Add an entry to FEATURE_REGISTRY below.
 *  2. Add the matching nav item to ALL_NAV_ITEMS in `useNavItems.ts` (if new).
 *  3. (Future phases) the registry will also drive onboarding categories and
 *     focus-mode profiles — for now those still need separate updates.
 *
 * Backward-compat notes:
 *  - Defaults must be `false` for opt-in features. Defaulting to `true` means
 *    every new feature lights up automatically for users who onboarded before
 *    the feature existed (the bug class this registry solves).
 *  - The `health` feature defaults to `true` for legacy reasons — existing
 *    users without explicit health.enabled = true expect Health to be visible.
 *    New features should not follow this pattern.
 */

import type { OnboardingPreferences, PrimaryUseCase } from "../types";

type PrefsLike = OnboardingPreferences | undefined | null;

export interface FeatureDef {
  /** Stable feature ID. Use kebab-case for multi-word names. Never change once shipped. */
  id: string;
  /** Nav item ID — must match an entry in ALL_NAV_ITEMS. */
  navItem: string;
  /** Returns true if this feature is enabled for the given user prefs. */
  isEnabled: (prefs: PrefsLike) => boolean;
  /** Optional global feature-flag name that gates this feature regardless of prefs. */
  requiredFlag?: string;
  /** primaryUseCase values that promote this feature into the primary nav slot. */
  primaryFor?: PrimaryUseCase[];
}

/**
 * Order matters for the nav bar's "extras" padding pass — earlier entries
 * are preferred when filling slots beyond the primary feature + community.
 */
export const FEATURE_REGISTRY: FeatureDef[] = [
  {
    id: "womens-health",
    navItem: "womens-health",
    isEnabled: (p) => !!p?.health?.cycle,
    primaryFor: ["health"],
  },
  {
    id: "fitness",
    navItem: "fitness",
    isEnabled: (p) => !!p?.health?.fitness,
    primaryFor: ["fitness", "weightloss"],
  },
  {
    id: "running",
    navItem: "running",
    isEnabled: (p) => !!p?.health?.running,
    primaryFor: ["running"],
  },
  {
    id: "notes",
    navItem: "notes",
    isEnabled: (p) => !!p?.notes,
    primaryFor: ["notes", "notesandtasks", "housework"],
  },
  {
    id: "tasks",
    navItem: "tasks",
    isEnabled: (p) => !!p?.tasks,
    primaryFor: ["notesandtasks", "housework"],
  },
  {
    id: "calendar",
    navItem: "calendar",
    isEnabled: (p) => !!p?.calendar,
  },
  {
    id: "health",
    navItem: "health",
    // Legacy default true — pre-existing onboarding docs without health.enabled
    // expect the Health page to be visible. Don't replicate this for new features.
    isEnabled: (p) => p?.health?.enabled ?? true,
  },
  {
    id: "pet",
    navItem: "pet",
    isEnabled: (p) => p?.virtualPet?.enabled ?? false,
    requiredFlag: "virtual_pet",
  },
  {
    id: "collection",
    navItem: "collection",
    isEnabled: (p) => p?.collection?.enabled ?? false,
    requiredFlag: "collection",
  },
];

/** Nav items that are always shown regardless of features (no gating). */
export const ALWAYS_AVAILABLE_NAV = ["badges", "tip-us", "community"];

/** Nav items that are always available in standard (non-focus) mode. */
export const STANDARD_MODE_EXTRAS = ["account", "family"];

/**
 * Resolve which feature should occupy the primary nav slot for a given
 * primaryUseCase. Returns undefined when no registry entry claims it —
 * caller should fall back to a sensible default (notes/tasks).
 */
export function findPrimaryFeature(useCase: PrimaryUseCase | null | undefined): FeatureDef | undefined {
  if (!useCase) return undefined;
  return FEATURE_REGISTRY.find((f) => f.primaryFor?.includes(useCase));
}
